// src/components/snapshot/strategy-brief/StrategyBriefScorecard.tsx

type Dimension = {
  id: string;
  label: string;
  score: number;
};

type Props = {
  score: { value: number; outOf: number; label: string };
  dimensions: Dimension[];
  interpretation: string;
};

const scoreColor = (value: number) => {
  if (value >= 80) return '#10B981';
  if (value >= 65) return '#18365E';
  if (value >= 50) return '#F59E0B';
  return '#EF4444';
};

const scoreBand = (value: number) => {
  if (value >= 80) return { label: 'Strong Position', color: '#10B981', bg: 'rgba(16,185,129,0.12)' };
  if (value >= 60) return { label: 'Moderate Optimization Opportunity', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' };
  return { label: 'High Optimization Opportunity', color: '#EF4444', bg: 'rgba(239,68,68,0.10)' };
};

export function StrategyBriefScorecard({ score, dimensions, interpretation }: Props) {
  const band = scoreBand(score.value);

  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white shadow-sm overflow-hidden break-inside-avoid">
      <div className="p-6 sm:p-7">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">

          {/* Left: score panel */}
          <div className="lg:flex-[0_0_420px]">
            <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
              {score.label}
            </div>

            <div className="mt-3 rounded-2xl border border-[#E5E7EB] bg-gradient-to-br from-white to-[#F5F7FA] p-5 sm:p-6">
              <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
                Overall Score
              </div>

              {/* Score number row */}
              <div className="mt-2 flex items-baseline gap-2">
                <div className="text-[72px] font-extrabold tracking-tight text-[#18365E] leading-none">
                  {score.value}
                </div>
                <div className="text-base font-bold text-[#717182] pb-1">
                  / {score.outOf}
                </div>
              </div>

              {/* Band pill — sits cleanly below the score */}
              <div className="mt-3">
                <div
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold"
                  style={{ backgroundColor: band.bg, color: band.color }}
                >
                  <span
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: band.color }}
                  />
                  {band.label}
                </div>
              </div>

              <p className="text-sm text-[#18365E]/85 leading-relaxed mt-4">
                {interpretation}
              </p>
            </div>
          </div>

          <div className="hidden lg:block w-px bg-[#E5E7EB]" />

          {/* Right: dimension breakdown */}
          <div className="flex-1">
            <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
              Dimension Breakdown
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              {dimensions.map((d) => {
                const color = scoreColor(d.score);
                return (
                  <div
                    key={d.id}
                    className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[12px] font-semibold text-[#18365E] leading-snug">
                        {d.label}
                      </div>
                      <div className="text-[12px] font-extrabold text-[#18365E] tabular-nums flex-shrink-0">
                        {d.score}
                      </div>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-white overflow-hidden ring-1 ring-[#E5E7EB]">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.min(100, Math.max(0, d.score))}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
